import { AxiosResponse } from "axios";
import { axiosPatientInstance } from "config";
import { useAxiosMutation } from "hooks";
import { useQuery, useQueryClient } from "react-query";

type ConversationLogStatus = "Recording" | "Completed" | "Cancelled" | "Failed";

interface StartConversationLogRequest {
  chartCode: string;
}

interface StartConversationLogResponse {
  conversationLogId: string;
  audioUploadUrl: string;
}

interface CompleteConversationLogRequest {
  transcriptText: string;
  audioDurationSeconds: number;
  chartStructure: Record<string, string[]> | null;
  availableOrders: string[];
}

interface SectionSuggestionDto {
  sectionSuggestionId: string;
  sectionCode: string;
  suggestionData: string;
  orderSuggestions: string;
  isReviewed: boolean;
}

interface CompleteConversationLogResponse {
  conversationLogId: string;
  status: string;
  suggestions: SectionSuggestionDto[];
}

interface ConversationLogListDto {
  conversationLogId: string;
  startTime: string;
  endTime: string | null;
  audioDurationSeconds: number;
  status: string;
}

interface ConversationLogListPageDto {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  content: ConversationLogListDto[] | null;
}

interface ConversationLogDetailDto {
  conversationLogId: string;
  startTime: string;
  endTime: string | null;
  transcriptText: string;
  audioS3Key: string;
  audioDurationSeconds: number;
  status: string;
}

interface AudioDownloadUrlDto {
  downloadUrl: string;
}

interface ReviewSuggestionResponseDto {
  success: boolean;
}

interface CompleteConversationLogMutationInput {
  conversationLogId: string;
  encounterId: string;
  request: CompleteConversationLogRequest;
}

interface AudioDownloadUrlMutationInput {
  conversationLogId: string;
}

interface ReviewSuggestionMutationInput {
  suggestionId: string;
  encounterId: string;
}

function useStartConversationLog(encounterId: string) {
  return useAxiosMutation<
    AxiosResponse<StartConversationLogResponse>,
    StartConversationLogRequest
  >(["startConversationLog", { encounterId }], (request) =>
    axiosPatientInstance.post<StartConversationLogResponse>(
      `v1/conversation-log/${encounterId}/start`,
      request
    )
  );
}

function useCompleteConversationLog() {
  const queryClient = useQueryClient();

  return useAxiosMutation<
    AxiosResponse<CompleteConversationLogResponse>,
    CompleteConversationLogMutationInput
  >(
    ["completeConversationLog"],
    ({ conversationLogId, request }) =>
      axiosPatientInstance.put<CompleteConversationLogResponse>(
        `v1/conversation-log/${conversationLogId}/complete`,
        request
      ),
    {
      onSuccess: (_data, { encounterId }) => {
        queryClient.invalidateQueries(["sectionSuggestions", { encounterId }]);
        queryClient.invalidateQueries(["conversationLogs", { encounterId }]);
      },
    }
  );
}

function useConversationLogs(
  encounterId: string,
  page: number,
  pageSize: number
) {
  return useQuery<ConversationLogListDto[], Error>(
    ["conversationLogs", { encounterId, page, pageSize }],
    () =>
      axiosPatientInstance
        .get<ConversationLogListPageDto>(
          `v1/conversation-log/${encounterId}/logs?page=${page}&pageSize=${pageSize}`
        )
        .then((response) => response.data.content ?? []),
    { enabled: !!encounterId }
  );
}

function useConversationLogDetail(conversationLogId: string) {
  return useQuery<ConversationLogDetailDto, Error>(
    ["conversationLogDetail", { conversationLogId }],
    () =>
      axiosPatientInstance
        .get<ConversationLogDetailDto>(
          `v1/conversation-log/${conversationLogId}`
        )
        .then((response) => response.data),
    { enabled: !!conversationLogId }
  );
}

function useAudioDownloadUrl() {
  return useAxiosMutation<
    AxiosResponse<AudioDownloadUrlDto>,
    AudioDownloadUrlMutationInput
  >(["audioDownloadUrl"], ({ conversationLogId }) =>
    axiosPatientInstance.get<AudioDownloadUrlDto>(
      `v1/conversation-log/${conversationLogId}/audio`
    )
  );
}

function useReviewSuggestion() {
  const queryClient = useQueryClient();

  return useAxiosMutation<
    AxiosResponse<ReviewSuggestionResponseDto>,
    ReviewSuggestionMutationInput
  >(
    ["reviewSuggestion"],
    ({ suggestionId, encounterId: _encounterId }) =>
      axiosPatientInstance.put<ReviewSuggestionResponseDto>(
        `v1/conversation-log/suggestions/${suggestionId}/review`
      ),
    {
      onSuccess: (_data, { encounterId }) => {
        queryClient.invalidateQueries(["sectionSuggestions", { encounterId }]);
      },
    }
  );
}

export {
  useStartConversationLog,
  useCompleteConversationLog,
  useConversationLogs,
  useConversationLogDetail,
  useAudioDownloadUrl,
  useReviewSuggestion,
};

export type {
  ConversationLogStatus,
  StartConversationLogRequest,
  StartConversationLogResponse,
  CompleteConversationLogRequest,
  SectionSuggestionDto,
  CompleteConversationLogResponse,
  ConversationLogListDto,
  ConversationLogDetailDto,
  AudioDownloadUrlDto,
  ReviewSuggestionResponseDto,
};
